import type { AnalyticsAdapter, AnalyticsEvent } from "./analytics";
import { analyticsUrl } from "./page-context";

type Gtag = (command: string, ...args: unknown[]) => void;

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: Gtag;
    [key: `ga-disable-${string}`]: boolean;
  }
}

export class Ga4Adapter implements AnalyticsAdapter {
  private configured = false;
  private executed = false;
  private script: HTMLScriptElement | undefined;
  private pageContext: Record<string, string> = {};

  constructor(private readonly measurementId: string) {}

  setConsent(granted: boolean): void {
    window[`ga-disable-${this.measurementId}`] = !granted;
    if (!granted) {
      this.script?.remove();
      this.script = undefined;
      // If the download hasn't completed, do not replay consented-but-unsent
      // events later after withdrawal. Preserve config and other tags' entries.
      const queue = window.dataLayer;
      if (queue) {
        for (let i = queue.length - 1; i >= 0; i--) {
          const entry = queue[i] as IArguments | undefined;
          if (
            entry?.[0] === "event" &&
            entry[2]?.send_to === this.measurementId
          )
            queue.splice(i, 1);
        }
      }
    }
  }

  publish(event: AnalyticsEvent): void {
    if (window[`ga-disable-${this.measurementId}`]) return;
    this.load();
    if (event.type === "page-view") {
      this.pageContext = {
        page_location: analyticsUrl(event.location, window.location.origin),
        page_title: event.title,
        page_referrer: analyticsUrl(event.referrer),
      };
      window.gtag("event", "page_view", {
        ...this.pageContext,
        send_to: this.measurementId,
      });
      return;
    }
    if (event.type === "newsletter-signup") {
      window.gtag("event", "newsletter_signup", {
        ...this.pageContext,
        send_to: this.measurementId,
      });
      return;
    }
    window.gtag("event", "form_submit", {
      ...this.pageContext,
      audience: event.audience,
      send_to: this.measurementId,
    });
  }

  private load(): void {
    window.dataLayer = window.dataLayer ?? [];
    // Google consumes Arguments commands, not rest-parameter arrays.
    if (typeof window.gtag !== "function") {
      window.gtag = function (_command: string, ..._args: unknown[]) {
        window.dataLayer.push(arguments);
      };
    }
    if (!this.configured) {
      window.gtag("js", new Date());
      window.gtag("config", this.measurementId, {
        send_page_view: false,
        page_location: analyticsUrl(window.location.href),
        page_referrer: analyticsUrl(document.referrer),
      });
      this.configured = true;
    }
    if (this.script || this.executed) return;
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(this.measurementId)}`;
    script.onload = () => {
      this.executed = true;
    };
    script.onerror = () => {
      script.remove();
      if (this.script === script) this.script = undefined;
    };
    this.script = script;
    document.head.appendChild(script);
  }
}
